import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from 'src/api/rpc/client';
import type {
  CreateAgentPageInput,
  ListAgentPagesInput,
  UpdateAgentPageInput,
} from '@erp/rpc';

/**
 * Hooks over org-configurable agent pages (`agentPages.*`). The default
 * 'visible' view powers the sidebar (audience-filtered); the 'manage' view
 * powers the settings admin card. Mutations invalidate both.
 */

function useInvalidateAgentPages() {
  const queryClient = useQueryClient();
  return function invalidateAgentPages() {
    void queryClient.invalidateQueries({
      queryKey: orpc.agentPages.list.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.agentPages.get.key(),
    });
  };
}

export function useAgentPages(options?: {
  view?: ListAgentPagesInput['view'];
  enabled?: boolean;
}) {
  return useQuery({
    ...orpc.agentPages.list.queryOptions({
      input: { view: options?.view ?? 'visible' },
    }),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Polling cadence for the sidebar run-status markers while the tab is
 * visible — the same 30s the task-page count badges use. Launching or
 * cancelling a run also invalidates this key so the marker flips at once.
 */
const AGENT_PAGE_STATUSES_POLL_INTERVAL_MS = 30_000;

export function useAgentPageStatuses(options?: { enabled?: boolean }) {
  return useQuery({
    ...orpc.agentPages.statuses.queryOptions(),
    enabled: options?.enabled ?? true,
    refetchInterval: AGENT_PAGE_STATUSES_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
}

export function useAgentPage(id: string | null | undefined) {
  return useQuery({
    ...orpc.agentPages.get.queryOptions({ input: { id: id ?? '' } }),
    enabled: !!id,
  });
}

export function useCreateAgentPage() {
  const invalidateAgentPages = useInvalidateAgentPages();
  return useMutation({
    mutationFn: (input: CreateAgentPageInput) =>
      rpcClient.agentPages.create(input),
    onSuccess: invalidateAgentPages,
  });
}

export function useUpdateAgentPage() {
  const invalidateAgentPages = useInvalidateAgentPages();
  return useMutation({
    mutationFn: (input: UpdateAgentPageInput) =>
      rpcClient.agentPages.update(input),
    onSuccess: invalidateAgentPages,
  });
}

export function useArchiveAgentPage() {
  const invalidateAgentPages = useInvalidateAgentPages();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      rpcClient.agentPages.archive({ id }),
    onSuccess: invalidateAgentPages,
  });
}
